/**
 * Financial calculation utilities for fianzas
 *
 * These functions are pure (no side effects, no data fetching).
 * Tarifas data must be passed as a parameter where needed.
 */

/**
 * Parse a value to a number, handling strings with commas
 * @param {any} value - Value to parse
 * @returns {number|null} Parsed number or null
 */
export const parseNumber = (value) => {
  if (value === null || value === undefined) return null;
  // Finite only — NaN/Infinity slip through ordered comparisons and poison
  // money sums. Mirrors comision_service.py parse_number.
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Convert date values to milliseconds timestamp
 * Handles numbers, strings, and Firestore Timestamps
 * @param {any} value - Date value to convert
 * @returns {number|null} Timestamp in milliseconds or null
 */
export const toTimestamp = (value) => {
  if (!value) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (value?.seconds) return value.seconds * 1000;
  if (typeof value?.toMillis === "function") return value.toMillis();
  return null;
};

/**
 * Factura ledger modes (fianza.facturas[].mode).
 * Canonical definition lives here because the vigente fold below is money math;
 * `utils/fianzaEventos` re-exports these so existing import sites keep working.
 *   - emision:   the original factura (facturas[0])
 *   - adicional: a factura for a difference (aumento) — the prior one stays valid
 *   - sustituye: fully replaces the prior valid factura (how a disminución works)
 */
export const FACTURA_MODES = {
  EMISION: "emision",
  ADICIONAL: "adicional",
  SUSTITUYE: "sustituye",
};

/**
 * The currently-valid factura set: an emisión or sustituye resets the base
 * (a sustituye supersedes everything prior); adicionales accrue on top.
 * @param {Array} facturas - fianza.facturas[]
 * @returns {Array} The vigente subset
 */
export const getFacturasVigentes = (facturas) => {
  let vigentes = [];
  for (const f of facturas || []) {
    if (f?.mode === FACTURA_MODES.SUSTITUYE || f?.mode === FACTURA_MODES.EMISION) {
      vigentes = [f];
    } else {
      vigentes.push(f);
    }
  }
  return vigentes;
};

/**
 * THE prima accessor for a fianza — the single source of truth.
 *
 * When the fianza carries a factura ledger (`facturas[]`), the prima vigente is
 * the sum of the vigente entries: that is the only reading that stays correct
 * after an endoso monetario (an aumento adds a differential factura, a
 * disminución replaces the base). The legacy candidate chain below is the
 * fallback for fianzas that predate the ledger, where the root fields ARE the
 * whole truth.
 *
 * Reading the ledger first matters: `xmlFianza.primas.prima` used to win, and it
 * is not updated by a manual endoso, so every consumer showed the pre-endoso
 * prima. Everything downstream (tarifa, comisión, reportes, Pulso) flows through
 * here, so fixing it here fixes all of them.
 *
 * @param {Object} fianza - Fianza object
 * @returns {number} Prima amount (0 if not found)
 */
export const getPrimaAmount = (fianza) => {
  const facturas = fianza?.facturas;
  if (Array.isArray(facturas) && facturas.length > 0) {
    return getFacturasVigentes(facturas).reduce(
      (sum, f) => sum + (parseNumber(f?.prima) || 0),
      0
    );
  }

  // Net before gross: comisión is earned on the prima alone, never on derechos,
  // gastos de expedición or IVA. `monto` is the CFDI Total as UploadFianza
  // stores it, xmlFactura.primas.primaTotal is that same total (see xmlParsers),
  // and xmlFianza.primas.primaTotal is prima + derechos + gastos + iva — any of
  // them ranked above a net reading inflates the comisión by ~20-35%.
  const candidates = [
    // Net — the prima proper.
    fianza?.xmlFianza?.primas?.prima,
    fianza?.xmlFactura?.primas?.prima,
    fianza?.montoPrima,
    // Gross — last resort only, when no net reading exists.
    fianza?.xmlFianza?.primas?.primaTotal,
    fianza?.xmlFactura?.primas?.primaTotal,
    fianza?.monto,
    fianza?.xmlFactura?.total,
  ];
  for (const candidate of candidates) {
    const num = parseNumber(candidate);
    if (typeof num === "number" && !Number.isNaN(num) && num !== 0) {
      return num;
    }
  }
  return 0;
};

/**
 * Extract emission date from fianza
 * This is the canonical date used for reporting
 * Falls back through multiple sources when xmlFianza is not available
 * @param {Object} fianza - Fianza object
 * @returns {number|null} Timestamp in milliseconds or null
 */
export const getFechaEmision = (fianza) => {
  return (
    toTimestamp(fianza?.xmlFianza?.fechaAutorizacion) ||
    toTimestamp(fianza?.xmlFactura?.fecha) ||
    toTimestamp(fianza?.inicioVigenciaFianza) ||
    toTimestamp(fianza?.createdAt)
  );
};

/**
 * Calculate tarifa percentage from prima and monto afianzado
 * Automatically annualizes when the fianza duration exceeds one year
 * (handles multi-year fianzas and prima mínima cases)
 * @param {number} prima - Prima amount
 * @param {number} montoAfianzado - Bond amount
 * @param {Object} [options] - Options
 * @param {boolean} [options.truncate=false] - Truncate to 2 decimals (for reports)
 * @param {Object} [options.vigencia] - Optional vigencia {inicio, fin} timestamps in ms
 * @returns {number|null} Tarifa percentage or null if cannot calculate
 */
export const calculateTarifa = (prima, montoAfianzado, options = {}) => {
  const { truncate = false, vigencia } = options;
  if (!montoAfianzado || montoAfianzado <= 0) return null;

  let tarifa = (prima / montoAfianzado) * 100;

  // Annualize if fianza is longer than one year
  if (vigencia?.inicio && vigencia?.fin) {
    const days = (vigencia.fin - vigencia.inicio) / (24 * 60 * 60 * 1000);
    if (days > 365) {
      tarifa = tarifa / (days / 365);
    }
  }

  if (truncate) {
    return Math.floor(tarifa * 100) / 100;
  }
  return tarifa;
};

/**
 * Find matching commission bracket (tramo) for a given tarifa
 * @param {number|null} tarifaValor - Tarifa percentage value
 * @param {Array} tablas - Array of tarifa tables, each with tramos array
 * @returns {Object|null} Matching tramo {min, max, comision} or null
 */
export const findTramo = (tarifaValor, tablas) => {
  if (tarifaValor === null || !tablas || tablas.length === 0) return null;

  const tramos = tablas
    .flatMap((t) => t.tramos || [])
    .map((t) => ({
      min: Number(t.min),
      max: Number(t.max),
      comision: Number(t.comision),
    }));

  tramos.sort((a, b) => b.min - a.min); // descend by min
  const highest = tramos[0] || null;

  // Find exact bracket match
  for (const tramo of tramos) {
    if (tarifaValor <= tramo.min && tarifaValor >= tramo.max) {
      return tramo;
    }
  }

  // Prima minima: above highest bracket, use highest comision
  if (tarifaValor > (highest?.min ?? Number.NEGATIVE_INFINITY)) {
    return highest;
  }

  // Fallback: closest lower bracket
  if (tramos.length > 0) {
    return tramos[tramos.length - 1];
  }

  return null;
};

/**
 * Select appropriate tarifa table based on emisor, subRamo, and tipo
 * Priority: Exact match (subRamo + tipo) > Default/Generic (no subRamo, no tipo)
 * @param {string} emisor - Emisor name
 * @param {string} subRamo - SubRamo value
 * @param {string} tipo - Tipo fianza value
 * @param {Array} tarifas - All tarifas from DataContext
 * @returns {Array} Selected tarifa tables
 */
export const selectTarifaTable = (emisor, subRamo, tipo, tarifas, emisorRfc = null) => {
  if (!tarifas || !Array.isArray(tarifas)) return [];

  // RFC-first: the fianza XML and its CFDI routinely spell the razón social
  // differently ("ASEGURADORA ASERTA" vs "..., S.A. DE C.V."), so a table
  // carrying emisorRfc matches on it; exact name covers older tables.
  const sameEmisor = (t) => {
    if (t.esGeneral) return false;
    if (emisorRfc && t.emisorRfc) return t.emisorRfc === emisorRfc;
    return t.emisor === emisor;
  };
  const tarifasEmisor = tarifas.filter(sameEmisor);
  const normalizedSubRamo = String(subRamo || "").toLowerCase();
  const normalizedTipo = String(tipo || "").toLowerCase();

  // STRICT MATCHING: Only match if BOTH SubRamo AND Tipo match exactly
  const tarifasExactas = tarifasEmisor.filter(
    (t) =>
      t.subRamo &&
      t.tipo &&
      t.subRamo.toLowerCase() === normalizedSubRamo &&
      t.tipo.toLowerCase() === normalizedTipo
  );

  // Fallback to default/generic tables (no SubRamo, no Tipo)
  const tarifasGenerales = tarifasEmisor.filter((t) => !t.subRamo && !t.tipo);

  if (tarifasExactas.length > 0) return tarifasExactas;
  if (tarifasGenerales.length > 0) return tarifasGenerales;
  // Tabla general: the org-wide default for afianzadoras without their own
  // table. A % priced off it is an ESTIMATE (pctEstimado, "por confirmar").
  return tarifas.filter((t) => t.esGeneral);
};

/**
 * Calculate full commission for a fianza
 * @param {number} prima - Prima amount
 * @param {number} tarifa - Tarifa percentage (already calculated, should be annualized)
 * @param {Array} tarifas - All tarifas from DataContext
 * @param {string} emisor - Emisor name
 * @param {string} subRamo - SubRamo value
 * @param {string} tipo - Tipo fianza value
 * @returns {Object} {comisionPct, comisionMonto, tramo, tablaSeleccionada, primaMinima}
 */
export const calculateComision = (prima, tarifa, tarifas, emisor, subRamo, tipo, emisorRfc = null) => {
  const tablaSeleccionada = selectTarifaTable(emisor, subRamo, tipo, tarifas, emisorRfc);
  const esGeneral = tablaSeleccionada.length > 0 && tablaSeleccionada.every((t) => t.esGeneral);

  // Detect prima mínima: tarifa exceeds the highest bracket
  let primaMinima = false;
  if (tarifa !== null && tablaSeleccionada.length > 0) {
    const allTramos = tablaSeleccionada.flatMap((t) => t.tramos || []);
    const highestMin = Math.max(...allTramos.map((t) => Number(t.min)));
    if (tarifa > highestMin) {
      primaMinima = true;
    }
  }

  const tramo = findTramo(tarifa, tablaSeleccionada);
  const comisionPct = tramo?.comision ?? null;
  const comisionMonto = comisionPct !== null ? (prima * comisionPct) / 100 : null;

  return {
    comisionPct,
    comisionMonto,
    tramo,
    tablaSeleccionada,
    primaMinima,
    // % from the Tabla general fallback = an estimate ("por confirmar").
    pctEstimado: Boolean(esGeneral && comisionPct !== null),
  };
};

/**
 * Diagnose WHY a ledger movement has no comisión %, walking the same
 * resolution chain calculateComision uses and reporting the first missing
 * link. Runs off the movement's own snapshotted facets (afianzadora, tarifa,
 * prima, montoAfianzado, subRamo, tipoFianza) — no contract read needed.
 *
 * @param {Object} mov - Ledger movement (orgs/{org}/comisiones doc)
 * @param {Array} tarifas - Org tarifa tables (DataContext useTarifas)
 * @returns {Object|null} { code, mensaje, sugerido? } — null when there is
 *   nothing to diagnose (pct present, manual override, or a reversa whose
 *   amount derives from the reversed movement rather than a table).
 */
export const diagnoseComisionPct = (mov, tarifas) => {
  if (!mov || mov.comisionPct != null || mov.manualOverride || mov.reversaDeMovId) {
    return null;
  }
  const todas = Array.isArray(tarifas) ? tarifas : [];
  const afianzadora = mov.afianzadora || '';

  if (!afianzadora) {
    return {
      code: 'SIN_AFIANZADORA',
      mensaje: 'La fianza no tiene afianzadora identificada: el XML de la fianza no está cargado.',
    };
  }
  if (mov.tarifa == null) {
    const falta = !(Number(mov.prima) > 0)
      ? 'la prima'
      : 'el monto afianzado';
    return {
      code: 'SIN_TARIFA_FIANZA',
      mensaje: `No se puede calcular la tarifa de la fianza: falta ${falta}. Revisa que el XML de la fianza esté cargado.`,
    };
  }
  if (todas.length === 0) {
    return {
      code: 'SIN_TABLAS',
      mensaje: 'No hay tablas de comisiones configuradas (Configuración → Comisiones).',
    };
  }

  // Mirror resolveComision: a convenio movement prices off the convenio-scope
  // tables, falling back to the org's own when those cannot price it. Without
  // this the two ámbitos diagnose each other's movements.
  const { propias, convenio } = partitionTarifas(todas);
  const pick = (lista) => selectTarifaTable(
    afianzadora, mov.subRamo, mov.tipoFianza, lista, mov.afianzadoraRfc
  );
  const tablaConvenio = mov.splitRol === SPLIT_CONVENIO ? pick(convenio) : [];
  const lista = tablaConvenio.length > 0 ? convenio : propias;
  const tabla = tablaConvenio.length > 0 ? tablaConvenio : pick(propias);
  if (tabla.length === 0) {
    const delEmisor = lista.filter((t) => {
      if (t.esGeneral) return false;
      if (mov.afianzadoraRfc && t.emisorRfc) return t.emisorRfc === mov.afianzadoraRfc;
      return t.emisor === afianzadora;
    });
    if (delEmisor.length > 0) {
      return {
        code: 'SIN_TABLA_COINCIDENTE',
        mensaje: `Hay tabla de ${afianzadora}, pero ninguna coincide con el sub-ramo/tipo de esta fianza y no existe Tabla general.`,
      };
    }
    return {
      code: 'SIN_TABLA_AFIANZADORA',
      mensaje: `No hay tabla de comisiones para ${afianzadora} ni Tabla general.`,
    };
  }

  const tramo = findTramo(mov.tarifa, tabla);
  if (!tramo) {
    return {
      code: 'TABLA_SIN_TRAMOS',
      mensaje: `La tabla de ${afianzadora} no tiene tramos.`,
    };
  }
  // Everything resolves NOW — the table appeared after this movement was
  // booked. Offer the % it would produce.
  const esGeneral = tabla.every((t) => t.esGeneral);
  return {
    code: 'CALCULABLE',
    mensaje: `Con las tablas actuales: ${tramo.comision}%${esGeneral ? ' (Tabla general, estimado)' : ''} — la tabla se agregó después de este movimiento.`,
    sugerido: Number(tramo.comision),
  };
};

/**
 * Extract emisor name from fianza, checking multiple sources
 * @param {Object} fianza - Fianza object
 * @returns {string} Emisor name or empty string
 */
export const getEmisorNombre = (fianza) => {
  return (
    fianza?.xmlFactura?.emisor?.nombre ||
    fianza?.xmlFianza?.emisor?.nombre ||
    ""
  );
};

/**
 * Extract subRamo from fianza
 * @param {Object} fianza - Fianza object
 * @returns {string} SubRamo value or empty string
 */
export const getSubRamo = (fianza) => {
  return fianza?.xmlFianza?.subRamo || fianza?.subRamo || "";
};

/**
 * Extract tipo fianza from fianza
 * @param {Object} fianza - Fianza object
 * @returns {string} Tipo value or empty string
 */
export const getTipoFianza = (fianza) => {
  return fianza?.tipoFianza || fianza?.xmlFianza?.tipo || "";
};

/**
 * Extract ramo from fianza.
 * Not used to select a tarifa table today (that matches on emisor + subRamo +
 * tipo) — it is carried for traceability and for slicing reports by ramo.
 * @param {Object} fianza - Fianza object
 * @returns {string} Ramo value or empty string
 */
export const getRamo = (fianza) => {
  return fianza?.xmlFianza?.ramo || fianza?.ramo || "";
};

/**
 * Extract the fiado (the principal) from fianza.
 * @param {Object} fianza - Fianza object
 * @returns {{nombre: string|null, rfc: string|null}}
 */
export const getFiado = (fianza) => {
  const f = fianza?.xmlFianza?.fiado || fianza?.fiado || null;
  return { nombre: f?.nombre || null, rfc: f?.rfc || null };
};

/**
 * Extract the beneficiario from fianza.
 * @param {Object} fianza - Fianza object
 * @returns {{nombre: string|null}}
 */
export const getBeneficiario = (fianza) => {
  const b = fianza?.xmlFianza?.beneficiario || fianza?.beneficiario || null;
  return { nombre: b?.nombre || null };
};

/**
 * Extract the emisor RFC (the afianzadora's tax id).
 * @param {Object} fianza - Fianza object
 * @returns {string|null}
 */
export const getEmisorRfc = (fianza) => {
  return (
    fianza?.xmlFactura?.emisor?.rfc ||
    fianza?.xmlFianza?.emisor?.rfc ||
    null
  );
};

/**
 * Extract the agente/corredor recorded on the fianza XML.
 * @param {Object} fianza - Fianza object
 * @returns {{nombre: string|null, rfc: string|null}}
 */
export const getAgente = (fianza) => {
  const a = fianza?.xmlFianza?.agente || null;
  return { nombre: a?.nombre || null, rfc: a?.rfc || null };
};

// ── Convenio comisión split (mirror comision_service.py) ─────────────────────
// When the beneficiario of a fianza has a convenio de proveedores, the
// afianzadora splits the agent comisión between the convenio (beneficiario's)
// agent and the fiado's agent. The base % is ALWAYS the fiado's agent's %.

export const SPLIT_CONVENIO = 'convenio';            // org holds the convenio → beneficiario%
export const SPLIT_FIADO_EXTERNO = 'fiado_externo';  // org is fiado's agent → fiado%

export const DEFAULT_PARTICIPACION = { beneficiario: 70, fiado: 30 };

export const TARIFA_AMBITO_CONVENIO = 'convenio';

/**
 * Split an org's tarifa docs into { propias, convenio }. Convenio-scope tables
 * (ambito === 'convenio') estimate what fiado agents get; propias (no ambito)
 * are the org's own negotiated tables. A convenio fianza prices off the
 * convenio tables first, falling back to propias as the default estimate
 * (always flagged pctEstimado — only a locked % is authoritative).
 */
export const partitionTarifas = (tarifas) => {
  const propias = [];
  const convenio = [];
  (tarifas || []).forEach((t) => {
    (t?.ambito === TARIFA_AMBITO_CONVENIO ? convenio : propias).push(t);
  });
  return { propias, convenio };
};

const validParticipacion = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  const beneficiario = parseNumber(entry.beneficiario);
  const fiado = parseNumber(entry.fiado);
  if (beneficiario == null || fiado == null) return null;
  if (beneficiario < 0 || beneficiario > 100 || fiado < 0 || fiado > 100) return null;
  return { beneficiario, fiado };
};

/**
 * The {beneficiario, fiado} split for an afianzadora. Precedence: the
 * convenio's own participacion map, else the org's defaults
 * (orgs/{org}/config/comisiones.participacionConvenio), else 70/30.
 * Emisor keys match by exact string, same convention as selectTarifaTable.
 */
export const resolveParticipacion = (afianzadora, orgConfig, convenioData) => {
  const sources = [
    convenioData?.participacion || {},
    orgConfig?.participacionConvenio || {},
  ];
  for (const source of sources) {
    const entry = validParticipacion(source[afianzadora]);
    if (entry) return entry;
  }
  return { ...DEFAULT_PARTICIPACION };
};

/**
 * {rol, factor, splitPct} for a fianza's comisión. rol is which side of a
 * convenio split the org is on (null = no split); factor multiplies the gross
 * comisión into the org's net share; splitPct is that share as a percentage.
 *
 * The beneficiario-convenio marker lives at either level: on the contract
 * (its beneficiario has an external convenio, so every fianza splits) or on
 * an individual fianza. Either one nets the org the fiado share.
 */
export const resolveSplit = (contractData, fianza, participacion) => {
  // A per-fianza override wins: the split is negotiated with the other agent
  // case by case (50/50 is common), not an org-wide constant.
  const part = validParticipacion(fianza?.participacionOverride) || participacion;
  if (contractData?.convenioId) {
    const pct = part.beneficiario;
    return { rol: SPLIT_CONVENIO, factor: pct / 100, splitPct: pct };
  }
  if (fianza?.beneficiarioConvenio || contractData?.beneficiarioConvenio) {
    const pct = part.fiado;
    return { rol: SPLIT_FIADO_EXTERNO, factor: pct / 100, splitPct: pct };
  }
  return { rol: null, factor: 1, splitPct: null };
};
